// Shared field definitions for the Add Contact and Contact Details (edit) forms.
// Base fields (name, email, phone, address...) still live in each form; everything
// here is rendered by ContactExtraSections / EducationSection so both forms stay in sync.

export const COUNTRY_OPTIONS = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Spain',
  'Italy', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark',
  'Finland', 'Ireland', 'Portugal', 'Poland', 'Greece', 'Czech Republic', 'Hungary', 'Romania',
  'Brazil', 'Mexico', 'Argentina', 'Chile', 'Colombia', 'Peru', 'China', 'Japan', 'South Korea',
  'Singapore', 'Malaysia', 'Thailand', 'Indonesia', 'Philippines', 'Vietnam', 'United Arab Emirates',
  'Saudi Arabia', 'Qatar', 'Kuwait', 'Oman', 'Bahrain', 'Israel', 'Turkey', 'Egypt', 'South Africa',
  'Nigeria', 'Kenya', 'Ghana', 'New Zealand', 'Russia', 'Ukraine', 'Pakistan', 'Bangladesh',
  'Sri Lanka', 'Nepal', 'Other',
];

export const INDUSTRY_OPTIONS = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing', 'Retail', 'Real Estate',
  'Consulting', 'Marketing & Advertising', 'Legal Services', 'Media', 'Hospitality',
  'Construction', 'Logistics', 'Agriculture', 'Government', 'Non-Profit', 'Other',
];

export const COMPANY_SIZE_OPTIONS = [
  '1-10 employees', '11-50 employees', '51-200 employees', '201-500 employees',
  '501-1000 employees', '1000+ employees',
];

export const CONTACT_STATUS_OPTIONS = ['New', 'Contacted', 'Qualified', 'Customer', 'Partner', 'Inactive'];

export const STATUS_COLORS = {
  New: '#3b82f6',
  Contacted: '#0ea5e9',
  Qualified: '#8b5cf6',
  Customer: '#10b981',
  Partner: '#f59e0b',
  Inactive: '#94a3b8',
};

export const EDUCATION_LEVELS = [
  'School (10th / SSLC)', 'Higher Secondary (12th / PUC)', 'Diploma', "Bachelor's Degree",
  "Master's Degree", 'Doctorate (PhD)', 'Certification', 'Other',
];

const yearOptionsNote = 'e.g., 2018';

// Sections shown for an individual person
export const PERSON_SECTIONS = [
  {
    key: 'personal',
    title: 'Personal Details',
    icon: 'badge',
    fields: [
      { name: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
      { name: 'maritalStatus', label: 'Marital Status', type: 'select', options: ['Single', 'Married', 'Divorced', 'Widowed', 'Prefer not to say'] },
      { name: 'nationality', label: 'Nationality', placeholder: 'e.g., Indian' },
      { name: 'languages', label: 'Languages Known', placeholder: 'e.g., English, Hindi, Kannada', helperText: 'Separate with commas' },
      { name: 'alternateEmail', label: 'Alternate Email', type: 'email', placeholder: 'e.g., john.personal@gmail.com' },
      { name: 'alternatePhone', label: 'Alternate Phone', type: 'phone', placeholder: 'e.g., +91 98765 43210' },
    ],
  },
  {
    key: 'professional',
    title: 'Professional Details',
    icon: 'work',
    fields: [
      { name: 'employer', label: 'Current Company / Employer', placeholder: 'e.g., Infosys Ltd' },
      { name: 'department', label: 'Department', placeholder: 'e.g., Engineering' },
      { name: 'experienceYears', label: 'Total Experience', type: 'select', options: ['Fresher', '0-1 years', '1-3 years', '3-5 years', '5-10 years', '10-15 years', '15+ years'] },
      { name: 'personIndustry', label: 'Industry', type: 'select', options: INDUSTRY_OPTIONS },
      { name: 'skills', label: 'Skills', placeholder: 'e.g., React, Sales, Accounting', helperText: 'Separate with commas', md: 12 },
    ],
  },
  {
    key: 'social',
    title: 'Social Profiles',
    icon: 'share',
    fields: [
      { name: 'twitterProfile', label: 'X / Twitter', type: 'url', placeholder: 'https://x.com/username' },
      { name: 'facebookProfile', label: 'Facebook', type: 'url', placeholder: 'https://facebook.com/username' },
      { name: 'instagramProfile', label: 'Instagram', type: 'url', placeholder: 'https://instagram.com/username' },
    ],
  },
];

// Sections shown for a company
export const COMPANY_SECTIONS = [
  {
    key: 'registration',
    title: 'Business Registration',
    icon: 'gavel',
    fields: [
      { name: 'legalName', label: 'Registered / Legal Name', placeholder: 'e.g., Acme Technologies Pvt Ltd' },
      { name: 'companyType', label: 'Company Type', type: 'select', options: ['Private Limited', 'Public Limited', 'LLP', 'Partnership', 'Sole Proprietorship', 'One Person Company', 'Government', 'NGO / Trust', 'Other'] },
      { name: 'registrationNumber', label: 'Registration No. (CIN)', placeholder: 'e.g., U72900KA2015PTC012345' },
      { name: 'gstNumber', label: 'GST Number', type: 'gst', placeholder: 'e.g., 29ABCDE1234F1Z5' },
      { name: 'panNumber', label: 'PAN', type: 'pan', placeholder: 'e.g., ABCDE1234F' },
      { name: 'foundedYear', label: 'Year Founded', type: 'year', placeholder: yearOptionsNote },
    ],
  },
  {
    key: 'business',
    title: 'Business Profile',
    icon: 'storefront',
    fields: [
      { name: 'annualRevenue', label: 'Annual Revenue', type: 'select', options: ['Under ₹1 Cr', '₹1–10 Cr', '₹10–50 Cr', '₹50–100 Cr', '₹100–500 Cr', '₹500 Cr+'] },
      { name: 'branchCount', label: 'Number of Branches', type: 'number', placeholder: 'e.g., 3' },
      { name: 'productsServices', label: 'Products / Services', placeholder: 'e.g., ERP software, IT consulting', md: 12 },
      { name: 'businessDescription', label: 'About the Company', type: 'multiline', md: 12, placeholder: 'What the company does, key customers, etc.' },
    ],
  },
  {
    key: 'contactPerson',
    title: 'Primary Contact Person',
    icon: 'person',
    fields: [
      { name: 'contactPersonName', label: 'Name', placeholder: 'e.g., Priya Sharma' },
      { name: 'contactPersonDesignation', label: 'Designation', placeholder: 'e.g., Procurement Head' },
      { name: 'contactPersonEmail', label: 'Email', type: 'email', placeholder: 'e.g., priya@acme.com' },
      { name: 'contactPersonPhone', label: 'Phone', type: 'phone', placeholder: 'e.g., +91 98765 43210' },
    ],
  },
  {
    key: 'social',
    title: 'Social Profiles',
    icon: 'share',
    fields: [
      { name: 'companyLinkedin', label: 'LinkedIn Page', type: 'url', placeholder: 'https://linkedin.com/company/acme' },
      { name: 'twitterProfile', label: 'X / Twitter', type: 'url', placeholder: 'https://x.com/acme' },
      { name: 'facebookProfile', label: 'Facebook', type: 'url', placeholder: 'https://facebook.com/acme' },
      { name: 'instagramProfile', label: 'Instagram', type: 'url', placeholder: 'https://instagram.com/acme' },
    ],
  },
];

// Relationship tracking, shown for both types
export const RELATIONSHIP_SECTION = {
  key: 'relationship',
  title: 'Relationship',
  icon: 'handshake',
  fields: [
    { name: 'contactStatus', label: 'Status', type: 'select', options: CONTACT_STATUS_OPTIONS },
    { name: 'leadSource', label: 'Source', type: 'select', options: ['Referral', 'Website', 'Social Media', 'Event / Exhibition', 'Cold Call', 'Email Campaign', 'Walk-in', 'Partner', 'Other'] },
    { name: 'preferredContactMethod', label: 'Preferred Contact Method', type: 'select', options: ['Phone', 'WhatsApp', 'Email', 'In Person'] },
  ],
};

export const getSections = (contactType) => [
  ...(contactType === 'company' ? COMPANY_SECTIONS : PERSON_SECTIONS),
  RELATIONSHIP_SECTION,
];

const ALL_EXTRA_FIELD_NAMES = [
  ...new Set(
    [...PERSON_SECTIONS, ...COMPANY_SECTIONS, RELATIONSHIP_SECTION].flatMap((s) => s.fields.map((f) => f.name))
  ),
];

export const EMPTY_EDUCATION = {
  level: '',
  institution: '',
  boardOrUniversity: '',
  course: '',
  fieldOfStudy: '',
  startYear: '',
  endYear: '',
  grade: '',
  currentlyStudying: false,
};

// Initial form values for the extra fields, from an existing contact or blank
export const getExtraInitialValues = (contact = {}) => {
  const values = {};
  ALL_EXTRA_FIELD_NAMES.forEach((name) => {
    values[name] = contact?.[name] ?? '';
  });
  values.education = Array.isArray(contact?.education)
    ? contact.education.map((entry) => ({ ...EMPTY_EDUCATION, ...entry }))
    : [];
  return values;
};

const isBlankEducation = (entry) =>
  !entry.level && !entry.institution && !entry.course && !entry.boardOrUniversity;

// Payload for the fields relevant to this contact type; blanks become null
export const buildExtraPayload = (formData, contactType) => {
  const payload = {};
  getSections(contactType).forEach((section) =>
    section.fields.forEach(({ name }) => {
      const value = formData[name];
      payload[name] = typeof value === 'string' ? value.trim() || null : value ?? null;
    })
  );
  if (contactType === 'person') {
    payload.education = (formData.education || [])
      .filter((entry) => !isBlankEducation(entry))
      .map((entry) => ({ ...entry, endYear: entry.currentlyStudying ? '' : entry.endYear }));
  }
  return payload;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\s\-+()]+$/;
const URL_RE = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;
const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const isValidYear = (value) => {
  const year = Number(value);
  return /^\d{4}$/.test(String(value)) && year >= 1800 && year <= new Date().getFullYear() + 10;
};

// Validates only the fields that were filled in; returns { fieldName: message }
export const validateExtraFields = (formData, contactType) => {
  const errors = {};
  getSections(contactType).forEach((section) =>
    section.fields.forEach(({ name, type, label }) => {
      const value = String(formData[name] ?? '').trim();
      if (!value) return;
      if (type === 'email' && !EMAIL_RE.test(value)) errors[name] = 'Enter a valid email address';
      if (type === 'phone' && (!PHONE_RE.test(value) || value.replace(/\D/g, '').length < 10)) errors[name] = 'Enter a valid phone number';
      if (type === 'url' && !URL_RE.test(value)) errors[name] = 'Enter a valid link';
      if (type === 'gst' && !GST_RE.test(value.toUpperCase())) errors[name] = 'GST number should be 15 characters, e.g. 29ABCDE1234F1Z5';
      if (type === 'pan' && !PAN_RE.test(value.toUpperCase())) errors[name] = 'PAN should look like ABCDE1234F';
      if (type === 'year' && !isValidYear(value)) errors[name] = `Enter a valid ${label.toLowerCase()}`;
      if (type === 'date' && new Date(value) > new Date()) errors[name] = 'Date cannot be in the future';
    })
  );

  if (contactType === 'person') {
    (formData.education || []).forEach((entry, index) => {
      if (isBlankEducation(entry)) return;
      if (!entry.institution?.trim()) errors[`education.${index}.institution`] = 'Institution name is required';
      if (entry.startYear && !isValidYear(entry.startYear)) errors[`education.${index}.startYear`] = 'Invalid year';
      if (!entry.currentlyStudying && entry.endYear && !isValidYear(entry.endYear)) errors[`education.${index}.endYear`] = 'Invalid year';
      if (!entry.currentlyStudying && entry.startYear && entry.endYear && Number(entry.endYear) < Number(entry.startYear)) {
        errors[`education.${index}.endYear`] = 'Must be after start year';
      }
    });
  }
  return errors;
};
